import './NewsRecommendationTile.scss';
import {
  buildRecommendationList,
  RecommendationList as HeadlessRecommendationList,
  Result,
} from '@coveo/headless/recommendation';
import { useEffect, useState, FunctionComponent } from 'react';
import { useCoveoRecommendationEngine } from '../../Coveo/CoveoRecommendationEngine';
import { Link } from '@lmig/lmds-react-link';
import { Heading } from '@lmig/lmds-react-typography';
import globalConfig from '../../../Data/globalConfig.json';

export function NewsRecommendationTile() {
  const engine = useCoveoRecommendationEngine();
  const [controller, setController] = useState<HeadlessRecommendationList | null>(null);
  const newsSearchResultsURL = `${globalConfig.Coveo.searchPage}?qtype=news`;

  useEffect(() => {
    if (engine.recommendationEngine) {
      console.log('Recommendation engine loaded');
      setController(buildRecommendationList(engine.recommendationEngine, { options: { numberOfRecommendations: 5 } }));
    }
  }, [engine.recommendationEngine]);

  return (
    <div id="news-recommendation-tile-container">
      <div className="header">
        <Heading type="h4" className="title">
          My news
        </Heading>
        <Link href={newsSearchResultsURL} className="see-all-link lm-link" variant="standalone">
          See all
        </Link>
      </div>
      <div className="latest-news-articles">
        {controller ? (
          <NewsRecommendationList controller={controller}></NewsRecommendationList>
        ) : (
          <div>No news recommendations</div>
        )}
      </div>
    </div>
  );
}

interface NewsRecommendationListProps {
  controller: HeadlessRecommendationList;
}

interface NewsRecommendationResult {
  clickUri: string;
  featuredImageSrc: string;
  headline: string;
  products: string[] | null;
  publishDate: string;
  searchDescription: string;
  states: string[] | null;
}

export const NewsRecommendationList: FunctionComponent<NewsRecommendationListProps> = ({ controller }) => {
  const [state, setState] = useState(controller.state);
  const [filteredRecommendations, setFilteredRecommendations] = useState<NewsRecommendationResult[]>([]);

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));
  }, [controller]);

  useEffect(() => {
    if (!state?.recommendations?.length && controller) {
      controller.refresh();
    } else if (state?.recommendations?.length) {
      setFilteredRecommendations(filterRecommendations(state.recommendations));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.recommendations.length]);

  if (state.error) {
    return (
      <div>
        <div>Oops {state.error.message}</div>
        <code>{JSON.stringify(state.error)}</code>
        <button onClick={() => controller.refresh()}>Try again</button>
      </div>
    );
  }

  const filterRecommendations = (recommendations: Result[]): NewsRecommendationResult[] => {
    let recs = new Set<NewsRecommendationResult>();
    recommendations.forEach((rec) => {
      let res = getRecommendationResult(rec);
      if (res && res.clickUri && res.headline) recs.add(res);
    });
    return [...recs];
  };

  const getRecommendationResult = (rec: Result): NewsRecommendationResult | null => {
    try {
      return {
        clickUri: rec.clickUri?.split('?')[0],
        featuredImageSrc: typeof rec.raw?.lmcimagesrc === 'string' ? rec.raw.lmcimagesrc : '',
        headline: typeof rec.raw?.lmcheadline === 'string' ? rec.raw.lmcheadline : '',
        products: Array.isArray(rec.raw?.lmcproducttags) ? rec.raw.lmcproducttags : null,
        publishDate: typeof rec.raw?.lmcpublishdate === 'string' ? rec.raw.lmcpublishdate : '',
        searchDescription: typeof rec.raw?.lmcsearchdescription === 'string' ? rec.raw.lmcsearchdescription : '',
        states: Array.isArray(rec.raw?.lmcstates) ? rec.raw.lmcstates : null,
      } as NewsRecommendationResult;
    } catch (e) {}
    return null;
  };

  return (
    <>
      {filteredRecommendations.length ? (
        filteredRecommendations.map((recommendation) => (
          <div>
            <Link
              href={recommendation.clickUri}
              className="news-article-link"
              variant="standalone"
              caretPosition="none"
            >
              {recommendation.featuredImageSrc && (
                <img src={recommendation.featuredImageSrc} alt={recommendation.headline} />
              )}
              <div>{recommendation.headline}</div>
            </Link>
            <hr />
          </div>
        ))
      ) : (
        <div className="no-latest-articles">No news article recommendations</div>
      )}
    </>
  );
};

export default NewsRecommendationTile;
